<template>
  <v-card no-body class="mt-4 pb-2">
    <v-card-title class="d-flex justify-space-between">
      <div class="d-flex">
        <h6 class="d-flex">
          <span class="d-flex flex-column">
          <span>{{ $t('sustainability_dashboard_ui.above_average_diesel_usage_on_route') }}</span>
        </span>
        </h6>
      </div>
      <info-modal>
        <v-card-title>
          <h4>{{ $t('sustainability_dashboard_ui.modal_info.route_reefer_activity_impact.title') }}</h4>
        </v-card-title>
        <v-card-text v-html="$t('sustainability_dashboard_ui.modal_info.route_reefer_activity_impact.title')"/>
      </info-modal>
    </v-card-title>
    <v-card-text>
      <div>
        <div class="card-stats">
          <div class="d-flex align-items-center">
            <v-icon>mdi-currency-usd</v-icon>
            <span>{{ formattedMetrics.costOverTarget }}</span>
          </div>
          <div class="d-flex align-items-center">
            <v-icon>mdi-gas-station</v-icon>
            <span>{{ formattedMetrics.dieselOverTarget }}</span>
          </div>
          <div class="d-flex align-items-center">
            <v-icon>mdi-smog</v-icon>
            <span>{{ formattedMetrics.overTargetCO2Emissions }}</span>
          </div>
        </div>
        <div class="d-flex justify-content-center">
          <span class="small mt-4 text-muted">({{ this.summaryString }} {{ this.runtimeString }})</span>
        </div>
        <div v-if="singleCustomerSelected">
          <hr>
          <div class="d-flex justify-content-around mt-4">
            <v-btn :to="'/sustainability/enroute?customer_id=' + selectedCustomerId" variant="link">
              {{ $t('route_details' )}}
            </v-btn>
            <v-btn :to="'/sustainability/vehicles?strategy=enroute&customer_id=' + selectedCustomerId" variant="link">
              {{ $t('vehicle_details' )}}
            </v-btn>
          </div>
        </div>
        <!--
        <hr>
        <div v-if="singleCustomerSelected">
          <v-row>
            <v-col>
              <v-row>
                <v-col class="target-score-label text-right">
                  <span v-html="$t('sustainability_dashboard_ui.routes_above_target')"></span>
                </v-col>
                <v-col class="target-score" :class="routesAboveTargetStatusClass">
                  {{ formattedMetrics.routesAboveTargetPct }}%
                </v-col>
              </v-row>
              <div class="text-center">
                ({{ $t('n_of_m_routes', { n: formattedMetrics.routesAboveTarget, m: formattedMetrics.totalRoutes } )}})
              </div>
            </v-col>
            <v-col>
              <v-row>
                <v-col class="target-score-label text-right">
                  <span v-html="$t('sustainability_dashboard_ui.enroute_diesel_power_activity_target')"></span>
                </v-col>
                <v-col class="target-score text-muted">
                  {{ formattedMetrics.customerDieselTargetPct }}%
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <div class="d-flex justify-content-around mt-4">
            <v-button :to="'/sustainability/enroute?customer_id=' + selectedCustomerId" variant="link">
              {{ $t('route_details' )}}
            </v-button>
            <v-button :to="'/sustainability/vehicles?customer_id=' + selectedCustomerId" variant="link">
              {{ $t('vehicle_details' )}}
            </v-button>
          </div>
        </div>
        <div v-else>
          <v-row>
            <v-col class="target-score-label text-right">
              <span v-html="$t('sustainability_dashboard_ui.routes_above_target')"></span>
            </v-col>
            <v-col class="target-score" :class="routesAboveTargetStatusClass">
              {{ formattedMetrics.routesAboveTargetPct }}%
            </v-col>
          </v-row>
          <div class="text-center">
            ({{ $t('n_of_m_routes', { n: formattedMetrics.routesAboveTarget, m: formattedMetrics.totalRoutes } )}})
          </div>
        </div> -->
      </div>
    </v-card-text>
  </v-card>
</template>

<script>

import InfoModal from '@/components/shared/InfoModal'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import SustainabilityMixin from '@/components/mixins/SustainabilityMixin'
import MathUtil from '@/util/MathUtil'
import { each, keys, get, first, cloneDeep } from 'lodash'

export default {
  name: 'EnrouteReeferActivity',
  components: {
    InfoModal
  },
  mixins: [
    CurrentUserMixin,
    SustainabilityMixin
  ],
  props: {
    loading: null,
    // The raw metrics data retrieved from the API
    metricsData: null
  },
  computed: {
    /**
     * We need to format certain things differently depending on whether a single customer is selected or not.
     */
    singleCustomerSelected () {
      return keys(this.metricsData).length === 1
    },
    /**
     * The selected customer, if a single customer is currently selected.
     */
    selectedCustomerId () {
      if (this.singleCustomerSelected) {
        return first(keys(this.metricsData))
      }
      return null
    },
    routesAboveTargetStatusClass () {
      const value = this.computedMetrics.routesAboveTargetPct
      if (value > 50) {
        return 'text-danger'
      } else if (value > 25) {
        return 'text-warning'
      } else {
        return 'text-success'
      }
    },
    formattedMetrics () {
      // return this.computedMetrics
      const metrics = cloneDeep(this.computedMetrics)
      const format = (x, label) => x.toLocaleString() + ' ' + this.$tc(label, x)

      metrics.costTotal = this.formatWholeCurrency(metrics.costTotal)
      metrics.costOverTarget = this.formatWholeCurrency(metrics.costOverTarget)
      metrics.totalDiesel = format(metrics.totalDiesel, this.dieselUnitLabelShort)
      metrics.dieselOverTarget = format(metrics.dieselOverTarget, this.dieselUnitLabelShort)
      metrics.totalCO2Emissions = format(metrics.totalCO2Emissions, this.weightFormatLabel)
      metrics.overTargetCO2Emissions = format(metrics.overTargetCO2Emissions, this.weightFormatLabel)

      return metrics
    },
    /**
     * Recompute the numeric metrics according to the user's and customer's settings.
     * This both converts existing settings and computes new ones (including CO2 metrics)
     * @returns Object
     */
    computedMetrics () {
      const metrics = this.getCustomerMetrics()
      const params = this.sustainabilityParams

      const totalDiesel = this.convertDieselFromLiters(metrics.totalDiesel, 0)
      const dieselOverTarget = this.convertDieselFromLiters(metrics.dieselOverTarget, 0)

      return {
        costTotal: totalDiesel * params.dieselPrice,
        costOverTarget: dieselOverTarget * params.dieselPrice,
        totalDiesel: totalDiesel,
        dieselOverTarget: dieselOverTarget,
        totalCO2Emissions: this.getCO2Emissions(metrics.totalDiesel, 0),
        overTargetCO2Emissions: this.getCO2Emissions(metrics.dieselOverTarget, 0),
        totalRoutes: metrics.totalRoutes,
        routesAboveTarget: metrics.routesAboveTarget,
        routesAboveTargetPct: metrics.routesAboveTargetPct,
        customerDieselTargetPct: metrics.customerDieselTargetPct
      }
    }
  },
  methods: {
    /**
     * Grabs the customer metrics. If multiple customers are selected, the metrics for all selected customers will be
     * tallied together.
     * @returns Object
     */
    getCustomerMetrics () {
      let results
      if (this.singleCustomerSelected) {
        const customerId = first(keys(this.metricsData))
        const data = get(this.metricsData, customerId)
        results = {
          customerDieselTargetPct: data.power_diesel_target_pct,
          totalDiesel: data.power_diesel_l,
          dieselOverTarget: data.power_diesel_over_target_l,
          totalRoutes: data.num_routes,
          routesAboveTarget: data.num_routes_over_target,
          routesAboveTargetPct: data.num_routes_over_target_pct,
          dieselTarget: data.power_diesel_target_pct
        }
      } else {
        results = {
          totalDiesel: 0,
          dieselOverTarget: 0,
          totalRoutes: 0,
          routesAboveTarget: 0
        }
        each(this.metricsData, (customer) => {
          results.totalDiesel += customer.power_diesel_l
          results.dieselOverTarget += customer.power_diesel_over_target_l
          results.totalRoutes += customer.num_routes
          results.routesAboveTarget += customer.num_routes_over_target
        })
        results.routesAboveTargetPct = MathUtil.percentage(results.routesAboveTarget, results.totalRoutes, 1)
      }
      return results
    }
  }
}
</script>

<style scoped lang="scss">
.card-stats-label {
  font-size: 1.2rem;
  font-weight: 400;
}
</style>
